import React, { useState, useEffect } from 'react';
import { X, ExternalLink, Loader, ScrollText, ShieldCheck } from 'lucide-react';

export default function ArticleModal({ article, API_URL, onClose }) {
    const [text, setText] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setText(null);

        const load = async () => {
            const token = localStorage.getItem('token');
            try {
                const res = await fetch(`${API_URL}/legifrance/get-article?id=${encodeURIComponent(article.id || article.cid || '')}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = await res.json();
                if (!cancelled) setText(data.article?.text || data.description || '<p>Aucun texte officiel disponible pour cet article.</p>');
            } catch (e) {
                if (!cancelled) setText('<p>Erreur lors du chargement du texte officiel.</p>');
            }
            if (!cancelled) setLoading(false);
        };

        load();
        return () => { cancelled = true; };
    }, [article?.id, article?.cid, API_URL]);

    return (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-3 sm:p-6" onClick={onClose}>
            <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-xl shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="p-4 sm:p-6 border-b bg-slate-50 flex justify-between items-start gap-3">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-1 rounded-full">
                                <ShieldCheck size={12} /> Texte officiel Légifrance
                            </span>
                            {article.section && <span className="text-xs text-slate-500 truncate max-w-[260px] sm:max-w-[400px]">{article.section}</span>}
                        </div>
                        <h2 className="font-bold text-lg sm:text-xl text-slate-800 truncate">
                            {article.num ? `Article ${article.num}` : 'Article'}
                        </h2>
                        <a
                            href={article.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-semibold mt-1"
                        >
                            Ouvrir sur Légifrance <ExternalLink size={12} />
                        </a>
                    </div>
                    <button onClick={onClose} aria-label="Fermer" className="p-2 hover:bg-gray-200 rounded-full transition-colors text-slate-600">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center p-10 text-slate-500">
                            <Loader size={32} className="animate-spin text-emerald-600 mb-3" />
                            <span className="text-sm">Chargement du texte officiel...</span>
                        </div>
                    ) : (
                        <div className="prose prose-sm max-w-none text-slate-800 leading-relaxed text-[15px]">
                            <div className="flex items-start gap-2 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                                <ScrollText size={14} className="mt-0.5 flex-shrink-0" />
                                <span>Texte extrait en temps réel depuis le service officiel Légifrance (Code du travail). Vérifiable avec la réponse de l'assistant en vis-à-vis.</span>
                            </div>
                            <div dangerouslySetInnerHTML={{ __html: text }} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}